import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Search, ArrowUpRight, BookOpen, ChevronRight, Sparkles, TrendingUp,
  Clock, Users, Zap,
} from "lucide-react";
import { SiteLayout } from "./SiteLayout";
import {
  getArticlesTree,
  type ArticleCategory,
  type ArticleSummary,
  buildArticlesHref,
  articlesHrefBase,
} from "@/lib/articles.functions";

type Dict = {
  eyebrow: string;
  title: string;
  titleItalic: string;
  intro: string;
  searchPlaceholder: string;
  seoLead: string;
  seoP1: string;
  seoP2: string;
  featured: string;
  featuredCta: string;
  browseEyebrow: string;
  browseTitle: string;
  browseTitleItalic: string;
  seeAll: (name: string) => string;
  emptyTitle: string;
  emptyBody: string;
  ctaEyebrow: string;
  ctaTitle: string;
  ctaTitleItalic: string;
  ctaBody: string;
  ctaPrimary: string;
  ctaSecondary: string;
  articlesCount: (n: number) => string;
  minRead: (m: number) => string;
  by: string;
  whyBanana: string;
  whyBananaP: string;
  writtenByExperts: string;
  writtenByExpertsP: string;
  updatedRegularly: string;
  updatedRegularlyP: string;
  actionable: string;
  actionableP: string;
};

type TreeData = { categories: ArticleCategory[]; articles: ArticleSummary[] };

export function ArticlesHub({ locale, dict, initialData }: { locale: string; dict: Dict; initialData?: TreeData }) {
  const fetchTree = useServerFn(getArticlesTree);
  const { data, isLoading } = useQuery({
    queryKey: ["articles-tree", locale],
    queryFn: () => fetchTree({ data: { locale } }),
    initialData,
    staleTime: 60_000,
  });
  const [q, setQ] = useState("");

  const categories = data?.categories ?? [];
  const articles = data?.articles ?? [];
  const roots = useMemo<ArticleCategory[]>(
    () => categories.filter((c) => c.parent_id === null),
    [categories],
  );
  const catById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const chainOf = (catId: string): ArticleCategory[] => {
    const chain: ArticleCategory[] = [];
    let cur = catById.get(catId);
    while (cur) { chain.unshift(cur); cur = cur.parent_id ? catById.get(cur.parent_id) : undefined; }
    return chain;
  };

  const articlePath = (a: ArticleSummary) => {
    const chain = chainOf(a.category_id);
    return buildArticlesHref(locale, [...chain.map((c) => c.slug), a.slug]);
  };

  const filteredArticles = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [] as ArticleSummary[];
    return articles
      .filter((a) => a.title.toLowerCase().includes(term) || (a.excerpt ?? "").toLowerCase().includes(term))
      .slice(0, 8);
  }, [q, articles]);

  const featured = articles[0];
  const featuredChain = featured ? chainOf(featured.category_id) : [];

  return (
    <SiteLayout>
      {/* HERO + SEARCH */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-14 pt-20 text-center md:px-6">
          <p className="inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
            {dict.eyebrow}
          </p>
          <h1 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl">
            {dict.title} <em className="italic">{dict.titleItalic}</em>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">{dict.intro}</p>
          <div className="relative mx-auto mt-8 max-w-2xl">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={dict.searchPlaceholder}
              className="w-full rounded-xl bg-muted px-14 py-5 text-base outline-none transition focus:bg-card focus:ring-2 focus:ring-foreground"
            />
            {filteredArticles.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-10 mt-2 overflow-hidden rounded-2xl bg-card text-left shadow-lg">
                {filteredArticles.map((a) => (
                  <Link key={a.id} to={articlePath(a) as any} className="flex items-start justify-between gap-3 px-5 py-3 transition hover:bg-muted">
                    <div>
                      <div className="text-sm font-bold">{a.title}</div>
                      {a.excerpt && <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{a.excerpt}</div>}
                    </div>
                    <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SEO INTRO STRIP */}
      <section className="bg-muted">
        <div className="mx-auto grid max-w-5xl gap-8 px-4 py-14 md:grid-cols-[1.2fr_1fr] md:px-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.whyBanana}</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">{dict.seoLead}</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground/80">{dict.seoP1}</p>
            <p className="mt-3 text-[15px] leading-relaxed text-foreground/80">{dict.seoP2}</p>
          </div>
          <div className="grid gap-3">
            <div className="rounded-2xl bg-card p-5">
              <Users className="h-5 w-5" />
              <p className="mt-3 text-sm font-bold">{dict.writtenByExperts}</p>
              <p className="mt-1 text-sm text-muted-foreground">{dict.writtenByExpertsP}</p>
            </div>
            <div className="rounded-2xl bg-card p-5">
              <Clock className="h-5 w-5" />
              <p className="mt-3 text-sm font-bold">{dict.updatedRegularly}</p>
              <p className="mt-1 text-sm text-muted-foreground">{dict.updatedRegularlyP}</p>
            </div>
            <div className="rounded-2xl bg-card p-5">
              <Zap className="h-5 w-5" />
              <p className="mt-3 text-sm font-bold">{dict.actionable}</p>
              <p className="mt-1 text-sm text-muted-foreground">{dict.actionableP}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="bg-background">
          <div className="mx-auto max-w-6xl px-4 py-14 md:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.featured}</p>
            <Link
              to={articlePath(featured) as any}
              className="group mt-4 grid overflow-hidden rounded-3xl bg-muted md:grid-cols-[1.1fr_1fr]"
            >
              {featured.cover_image_url ? (
                <div className="aspect-[16/10] bg-muted md:aspect-auto">
                  <img src={featured.cover_image_url} alt={featured.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]" width={1600} height={1000} />
                </div>
              ) : (
                <div className="flex aspect-[16/10] items-center justify-center bg-foreground/5 md:aspect-auto">
                  <Sparkles className="h-10 w-10 text-foreground/30" />
                </div>
              )}
              <div className="flex flex-col justify-center p-8 md:p-12">
                <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  {featuredChain.map((c, i) => (
                    <span key={c.id} className="inline-flex items-center gap-1.5">
                      {i > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
                      <span>{c.name}</span>
                    </span>
                  ))}
                </div>
                <h3 className="mt-3 font-display text-2xl leading-tight md:text-3xl">{featured.title}</h3>
                {featured.excerpt && <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{featured.excerpt}</p>}
                <div className="mt-6 flex items-center gap-2 text-sm font-bold">
                  <span>{dict.featuredCta}</span>
                  <ArrowUpRight className="h-4 w-4 transition group-hover:rotate-12" />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* CATEGORIES with articles inline */}
      <section className="bg-muted">
        <div className="mx-auto max-w-6xl px-4 py-16 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.browseEyebrow}</p>
          <h2 className="mt-2 font-display text-3xl md:text-4xl">
            {dict.browseTitle} <em className="italic">{dict.browseTitleItalic}</em>
          </h2>

          {isLoading ? (
            <div className="mt-10 space-y-10">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i}>
                  <div className="h-6 w-64 animate-pulse rounded-full bg-card" />
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div key={j} className="h-64 animate-pulse rounded-2xl bg-card" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : roots.length === 0 ? (
            <div className="mt-8 rounded-2xl bg-card p-10 text-center">
              <p className="font-display text-xl">{dict.emptyTitle}</p>
              <p className="mt-2 text-sm text-muted-foreground">{dict.emptyBody}</p>
            </div>
          ) : (
            <div className="mt-10 space-y-14">
              {roots.map((cat) => {
                const descIds = new Set([cat.id, ...collectDescendants(categories, cat.id)]);
                const catArticles = articles.filter((a) => descIds.has(a.category_id)).slice(0, 3);
                return (
                  <div key={cat.id}>
                    <div className="flex items-end justify-between gap-4">
                      <div>
                        <Link
                          to={buildArticlesHref(locale, [cat.slug]) as any}
                          className="flex items-center gap-2 font-display text-2xl transition hover:underline md:text-3xl"
                        >
                          <BookOpen className="h-5 w-5 opacity-60" />
                          {cat.name}
                        </Link>
                        {cat.description && (
                          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{cat.description}</p>
                        )}
                      </div>
                      <Link
                        to={buildArticlesHref(locale, [cat.slug]) as any}
                        className="hidden shrink-0 items-center gap-1 rounded-xl bg-card px-4 py-2 text-xs font-bold transition hover:bg-foreground hover:text-background sm:inline-flex"
                      >
                        {dict.seeAll(cat.name)} <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                    {catArticles.length > 0 ? (
                      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {catArticles.map((a) => (
                          <ArticleCard key={a.id} article={a} href={articlePath(a)} dict={dict} />
                        ))}
                      </div>
                    ) : (
                      <div className="mt-6 rounded-2xl bg-card p-6 text-sm text-muted-foreground">
                        {dict.emptyBody}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-16 md:px-6">
          <div className="rounded-3xl bg-muted p-8 md:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.ctaEyebrow}</p>
            <h3 className="mt-2 font-display text-2xl md:text-3xl">
              {dict.ctaTitle} <em className="italic">{dict.ctaTitleItalic}</em>
            </h3>
            <p className="mt-3 max-w-xl text-sm text-muted-foreground">{dict.ctaBody}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://app.banana.cards" target="_blank" rel="noopener" className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background transition hover:opacity-90">
                {dict.ctaPrimary} <ArrowUpRight className="h-4 w-4" />
              </a>
              <Link to={articlesHrefBase(locale) as any} className="inline-flex items-center gap-2 rounded-xl bg-card px-5 py-3 text-sm font-bold transition hover:bg-foreground hover:text-background">
                <TrendingUp className="h-4 w-4" /> {dict.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function ArticleCard({ article, href, dict }: { article: ArticleSummary; href: string; dict: Dict }) {
  return (
    <Link to={href as any} className="group flex h-full flex-col overflow-hidden rounded-2xl bg-card transition hover:-translate-y-0.5">
      {article.cover_image_url ? (
        <div className="aspect-[16/10] overflow-hidden bg-muted">
          <img src={article.cover_image_url} alt={article.title} loading="lazy" width={800} height={500} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.05]" />
        </div>
      ) : (
        <div className="flex aspect-[16/10] items-center justify-center bg-muted">
          <Sparkles className="h-8 w-8 text-foreground/30" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <h4 className="font-display text-lg leading-tight transition group-hover:underline">{article.title}</h4>
        {article.excerpt && (
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{article.excerpt}</p>
        )}
        <div className="mt-auto flex items-center gap-3 pt-4 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {article.author_name && <span>{dict.by} {article.author_name}</span>}
          {article.read_minutes ? <span>· {dict.minRead(article.read_minutes)}</span> : null}
        </div>
      </div>
    </Link>
  );
}

function collectDescendants(all: ArticleCategory[], id: string): string[] {
  const kids = all.filter((c) => c.parent_id === id);
  return kids.flatMap((k) => [k.id, ...collectDescendants(all, k.id)]);
}